import {useParams} from "react-router-dom";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {ContestDetailResponse, fetchContest, updatePredictions} from "../../api/api";
import React, {Fragment, useEffect, useMemo, useState} from "react";
import {Alert, Button, Card, Fade, Spinner, Stack} from "react-bootstrap";
import Form from "react-bootstrap/Form";
import {PredictionCard} from "./PredictionCard";
import {Page} from "../../common/Page";

export const LoadingSpinner = () => (
  <div className="d-flex justify-content-center p-4">
    <Spinner animation="border" role="status">
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  </div>
);

export const ContestPage = () => {
  const contestId = useParams().contestId || '';

  const {data: contest, isLoading, error} = useQuery(["contests", contestId],
    (_context) => fetchContest({id: contestId}),
    {
      enabled: !!contestId,
      retry: false
    })

  // TODO: build on this component for holding non-loaded state
  if (!contest) {
    if (isLoading) {
      return <LoadingSpinner/>
    }
    if (error) {
      return <Alert variant="danger">Error occurred</Alert>
    }
    return null;
  }

  const numberResolvedPredictions = contest.predictions.filter(prediction => !!prediction.outcome).length
  const hasSomeTrueFalseResults = contest.predictions.filter(prediction => prediction.outcome === 'true' || prediction.outcome === 'false').length > 0;
  const hasAllResults = contest.predictions.filter(prediction => !!prediction.outcome).length === contest.predictionCount;

  return <Page title={contest.name}
               breadcrumbs={{
                 parents: [{
                   title: "Home",
                   link: "/"
                 }]
               }}>
    {contest.description && <p>{contest.description}</p>}
    {contest.isOpen && contest.instructions && <p>{contest.instructions}</p>}
    <p>This contest has {contest.predictionCount} prediction{contest.predictionCount > 1 && "s"}.</p>

    {contest.isOpen ?
      <Fragment>
        <Alert variant={"primary"} className={"mb-3"}>
          You can edit your predictions at any time before the
          deadline on {new Date(contest.submissionDeadline).toLocaleDateString()}.
        </Alert>
        <ResponseForm contest={contest}/>
      </Fragment>
      :
      <Fragment>
        {(hasSomeTrueFalseResults || hasAllResults) ?
          <Card bg={"primary"} className={"mb-3"} text={"white"}>
            <Card.Header>{hasAllResults ? "Final result" : "Partial results"}</Card.Header>
            <Card.Body>
              {hasAllResults ?
                <Card.Text>
                  Every prediction has been resolved.
                </Card.Text> :
                <Card.Text>
                  {numberResolvedPredictions > 1 ? `${numberResolvedPredictions} predictions have been resolved.` :
                    'One prediction has been resolved.'}
                </Card.Text>
              }
              <Card.Text>You're in <b>{formatOrdinal(contest.participantPosition)} place</b>.</Card.Text>
              <Button variant={"outline-light"} href={`/predictions/contests/${contestId}/leaderboard`}>
                View leaderboard
              </Button>
            </Card.Body>
          </Card>
          :

          <Alert variant={"secondary"} className={"mb-3"}>
            This contest is now closed. Predictions can no longer be changed.
          </Alert>
        }

        {contest.predictions.map((prediction, index) => (
            <PredictionCard contestId={contest.id}
                            prediction={prediction}
                            key={index}
                            value={prediction.value}
                            setValue={() => {
                            }}
                            disabled={true}
                            showDetailsLink={!contest.isOpen}
                            showResolution={true}
            />
          )
        )}
      </Fragment>
    }
  </Page>
}

type FormValues = {
  [key: number]: number
}

function generateInitialFormValues(contest: ContestDetailResponse) {
  const initialValues: FormValues = {};
  contest.predictions.forEach(prediction => {
    initialValues[prediction.ordinal] = prediction.value;
  });
  return initialValues;
}

const ResponseForm = ({contest}: { contest: ContestDetailResponse }) => {
  const initialValues = generateInitialFormValues(contest);
  const [values, setValues] = useState<FormValues>(initialValues);
  const [submitting, setSubmitting] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const containsEdits = useMemo(
    () => Object.keys(values).some(key => values[Number(key)] !== initialValues[Number(key)]),
    [values, initialValues]);
  const queryClient = useQueryClient();
  const updatePredictionsMutation = useMutation(updatePredictions)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    updatePredictionsMutation.mutateAsync({id: contest.id, predictionResponses: values})
      .then(() => {
        setSubmitting(false);
        setLastSavedAt(Date.now());
        return queryClient.invalidateQueries({queryKey: ["contests", contest.id]});
      })
      .catch(() => {
        setError("Failed to save responses");
        setSubmitting(false);
      });
  }

  return <Form onSubmit={handleSubmit}>
    {contest.predictions.map((prediction, index) => (
        <PredictionCard contestId={contest.id}
                        prediction={prediction}
                        key={index}
                        value={values[prediction.ordinal]}
                        setValue={(newValue: number) =>
                          setValues(prev => ({
                            ...prev,
                            [prediction.ordinal]: newValue
                          }))}
                        disabled={false}
                        showDetailsLink={false}
                        showResolution={false}/>
      )
    )}
    <p>
      Responding as {contest.participantName}.
    </p>
    <Stack direction={"horizontal"} gap={3}>
      <Button variant={"primary"}
              type={"submit"}
              disabled={submitting}>Save</Button>
      <SaveStatus lastSavedAt={lastSavedAt} containsEdits={containsEdits}/>
    </Stack>
    {error && <Alert variant="danger" className={"mt-3"}>{error}</Alert>}
  </Form>
}

const SaveStatus = ({containsEdits, lastSavedAt}: { containsEdits: boolean; lastSavedAt: number | null }) => {
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    if (lastSavedAt) {
      setShowSaved(true);

      const timer = setTimeout(() => {
        setShowSaved(false);
      }, 3000); // show for 3 seconds

      return () => clearTimeout(timer);
    }
  }, [lastSavedAt]);

  return <Fragment>
    <Fade in={containsEdits || showSaved} mountOnEnter={true} unmountOnExit={true}>
      <span className="text-muted ms-3">
        {containsEdits ? "You have unsaved changes" : "All changes saved"}
      </span>
    </Fade>
  </Fragment>
};

function formatOrdinal(n: number, locale: string = "en-US"): string {
  const pluralRules = new Intl.PluralRules(locale, {type: "ordinal"});
  const suffixes: Record<Intl.LDMLPluralRule, string> = {
    one: "st",
    two: "nd",
    few: "rd",
    other: "th",
    many: "th", // included for completeness
    zero: "th"
  };

  const rule = pluralRules.select(n);
  const suffix = suffixes[rule] || "th";

  return `${n}${suffix}`;
}
